import { useState, useEffect, useCallback } from "react";
import { JUDGES_API, fetchWithAuth } from "../utils/api";

export function useJudgeProfiles() {
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatJudgeItem = (item) => {
    const judgeId = item.SK
      ? item.SK.replace("JUDGE#", "")
      : item.judgeId || item.id;

    return {
      id: judgeId,
      name: item.name || "",
      title: item.title || "",
      defaultNote: item.defaultNote || "",
      photoUrl: item.photoUrl, // Use public URLs directly
    };
  };

  const fetchJudges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWithAuth(JUDGES_API);
      
      // Format judges - no need for photo URL processing with public URLs
      const mapped = data.map(formatJudgeItem);
      
      setJudges(mapped);
    } catch (err) {
      setError(err.message || "Failed to fetch judges");
      console.error("Error fetching judges:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createJudge = useCallback(async (judgeData) => {
    try {
      setLoading(true);
      setError(null);
      const newItem = await fetchWithAuth(JUDGES_API, {
        method: "POST",
        body: JSON.stringify(judgeData)
      });
      const formattedItem = formatJudgeItem(newItem);
      setJudges((prev) => [...prev, formattedItem]);
      return newItem;
    } catch (err) {
      setError(err.message || "Failed to create judge");
      console.error("Error creating judge:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateJudge = useCallback(async (judgeId, updates) => {
    try {
      setLoading(true);
      setError(null);
      const updatedItem = await fetchWithAuth(`${JUDGES_API}/${judgeId}`, {
        method: "PUT",
        body: JSON.stringify(updates)
      });
      const formattedItem = formatJudgeItem(updatedItem);
      setJudges((prev) =>
        prev.map((judge) => (judge.id === judgeId ? formattedItem : judge))
      );
      return updatedItem;
    } catch (err) {
      setError(err.message || "Failed to update judge");
      console.error("Error updating judge:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteJudge = useCallback(async (judgeId) => {
    try {
      setLoading(true);
      setError(null);
      await fetchWithAuth(`${JUDGES_API}/${judgeId}`, {
        method: "DELETE"
      });
      setJudges((prev) => prev.filter((judge) => judge.id !== judgeId));
    } catch (err) {
      setError(err.message || "Failed to delete judge");
      console.error("Error deleting judge:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch judges on mount
  useEffect(() => {
    fetchJudges();
  }, [fetchJudges]);

  return {
    judges,
    loading,
    error,
    createJudge,
    updateJudge,
    deleteJudge,
    refreshJudges: fetchJudges, // Simplified refresh
  };
}